if (!customElements.get('bt-product-complementary')) {
  customElements.define(
    'bt-product-complementary',
    class BTProductComplementary extends HTMLElement {
      constructor() {
        super();

        this.onSelectionChange = this.onSelectionChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onOptionValueChange = this.onOptionValueChange.bind(this);
        this.onCurrentQuantityChange = this.onCurrentQuantityChange.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('[data-bt-bundle-form]');
        this.submitButton = this.querySelector('[data-bt-bundle-submit]');
        this.buttonText = this.querySelector('[data-bt-button-text]');
        this.status = this.querySelector('[data-bt-bundle-status]');
        this.count = this.querySelector('[data-bt-selected-count]');
        this.total = this.querySelector('[data-bt-bundle-total]');
        this.currentItem = this.querySelector('[data-current-item]');
        this.defaultButtonText = this.buttonText?.textContent.trim() || '';
        this.currencyFormatter = this.createCurrencyFormatter();

        this.addEventListener('change', this.onSelectionChange);
        this.form?.addEventListener('submit', this.onSubmit);
        this.mainQuantityInput = this.getMainProductInfo()?.querySelector('.quantity__input');
        this.mainQuantityInput?.addEventListener('change', this.onCurrentQuantityChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
          this.optionChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.onOptionValueChange
          );
          this.quantityUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.quantityUpdate,
            this.onCurrentQuantityChange
          );
        }

        this.hydrateCurrentVariant();
        this.syncCurrentPurchaseState();
        this.updateSummary();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onSelectionChange);
        this.form?.removeEventListener('submit', this.onSubmit);
        this.mainQuantityInput?.removeEventListener('change', this.onCurrentQuantityChange);
        this.variantChangeUnsubscriber?.();
        this.optionChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
      }

      createCurrencyFormatter() {
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency',
            currency: this.dataset.currency || 'INR',
          });
        } catch (_error) {
          return null;
        }
      }

      formatMoney(cents) {
        const amount = Number(cents || 0) / 100;

        if (this.currencyFormatter) return this.currencyFormatter.format(amount);
        return `${this.dataset.currency || ''} ${amount.toFixed(2)}`.trim();
      }

      getItems() {
        return Array.from(this.querySelectorAll('[data-bt-bundle-item]'));
      }

      getMainProductInfo() {
        return document.querySelector(
          `product-info.bt-product-page[data-product-id="${this.dataset.productId}"]`
        );
      }

      getSelectedItems() {
        return this.getItems().filter((item) => {
          if (item.hasAttribute('data-current-item')) return item.dataset.available === 'true';

          const checkbox = item.querySelector('[data-bt-item-toggle]');
          return Boolean(checkbox?.checked && !checkbox.disabled && item.dataset.available === 'true');
        });
      }

      syncCurrentPurchaseState() {
        if (!this.currentItem) return;

        const productInfo = this.getMainProductInfo();
        const variantInput = productInfo?.querySelector('product-form input[name="id"]');
        const submitButton = productInfo?.querySelector(
          `#ProductSubmitButton-${CSS.escape(productInfo?.dataset.section || '')}`
        );
        const quantityInput = productInfo?.querySelector('.quantity__input');
        const quantity = Math.max(0, Number.parseInt(quantityInput?.value || '1', 10) || 0);
        const available =
          Boolean(variantInput?.value) &&
          !variantInput.disabled &&
          quantity > 0 &&
          !submitButton?.disabled &&
          submitButton?.getAttribute('aria-disabled') !== 'true';

        this.currentItem.dataset.variantId = variantInput?.value || '';
        this.currentItem.dataset.quantity = String(quantity);
        this.currentItem.dataset.available = String(available);
        this.currentItem
          .querySelector('.bt-product-complementary__card')
          ?.classList.toggle('is-unavailable', !available);

        const quantityLabel = this.currentItem.querySelector('[data-bt-item-quantity]');
        if (quantityLabel) {
          quantityLabel.hidden = quantity <= 1;
          quantityLabel.textContent = `× ${quantity}`;
        }

        const soldOut = this.currentItem.querySelector('[data-bt-sold-out]');
        if (soldOut) soldOut.hidden = available;
      }

      onCurrentQuantityChange() {
        this.syncCurrentPurchaseState();
        this.clearStatus();
        this.updateSummary();
      }

      onSelectionChange(event) {
        if (event.target.matches('[data-bt-item-variant]')) {
          this.onRecommendationVariantChange(event.target);
          return;
        }

        if (!event.target.matches('[data-bt-item-toggle]')) return;

        const item = event.target.closest('[data-bt-bundle-item]');
        item?.querySelector('.bt-product-complementary__card')?.classList.toggle('is-selected', event.target.checked);
        this.clearStatus();
        this.updateSummary();
      }

      onRecommendationVariantChange(select) {
        const item = select.closest('[data-bt-bundle-item]');
        const option = select.selectedOptions[0];
        if (!item || !option) return;

        const available = option.dataset.available === 'true';
        item.dataset.variantId = option.value;
        item.dataset.price = option.dataset.price || '0';
        item.dataset.quantity = option.dataset.quantity || '1';
        item.dataset.available = String(available);

        const checkbox = item.querySelector('[data-bt-item-toggle]');
        if (checkbox) {
          checkbox.disabled = !available;
          if (!available) checkbox.checked = false;
        }

        const card = item.querySelector('.bt-product-complementary__card');
        card?.classList.toggle('is-unavailable', !available);
        card?.classList.toggle('is-selected', Boolean(checkbox?.checked && available));

        const price = item.querySelector('[data-bt-item-price]');
        if (price) price.textContent = this.formatMoney(option.dataset.price);

        const quantity = Number.parseInt(option.dataset.quantity || '1', 10) || 1;
        const quantityLabel = item.querySelector('[data-bt-item-quantity]');
        if (quantityLabel) {
          quantityLabel.hidden = quantity <= 1;
          quantityLabel.textContent = `× ${quantity}`;
        }

        const comparePrice = item.querySelector('[data-bt-compare-price]');
        if (comparePrice) {
          const compareAmount = Number(option.dataset.compareAtPrice || 0);
          const priceAmount = Number(option.dataset.price || 0);
          const hasComparePrice = compareAmount > priceAmount;
          comparePrice.hidden = !hasComparePrice;
          comparePrice.textContent = hasComparePrice ? this.formatMoney(compareAmount) : '';
        }

        const soldOut = item.querySelector('[data-bt-sold-out]');
        if (soldOut) soldOut.hidden = available;

        const image = item.querySelector('.bt-product-complementary__image');
        if (image && option.dataset.imageUrl) {
          image.src = option.dataset.imageUrl;
          image.removeAttribute('srcset');
          if (option.dataset.imageAlt) image.alt = option.dataset.imageAlt;
        }

        this.clearStatus();
        this.updateSummary();
      }

      updateSummary() {
        const selectedItems = this.getSelectedItems();
        const selectedCount = selectedItems.length;
        const total = selectedItems.reduce(
          (sum, item) => sum + Number(item.dataset.price || 0) * Number(item.dataset.quantity || 1),
          0
        );
        const itemLabel = selectedCount === 1 ? this.dataset.itemSingular : this.dataset.itemPlural;
        const currentAvailable = this.currentItem?.dataset.available === 'true';

        if (this.count) this.count.textContent = `${selectedCount} ${itemLabel || ''}`.trim();
        if (this.total) this.total.textContent = this.formatMoney(total);
        if (this.submitButton && !this.submitButton.classList.contains('is-loading')) {
          this.submitButton.disabled = !currentAvailable || Boolean(this.hasSubmitted);
          if (this.buttonText) {
            if (this.hasSubmitted) {
              this.buttonText.textContent = this.dataset.addedButtonLabel || this.defaultButtonText;
            } else {
              this.buttonText.textContent =
                currentAvailable && selectedCount > 0
                  ? `${this.defaultButtonText} · ${this.formatMoney(total)}`
                  : this.defaultButtonText;
            }
          }
        }
      }

      onOptionValueChange(event) {
        const picker = event?.data?.target?.closest?.('variant-selects');
        const productInfo = this.getMainProductInfo();
        if (!picker || !productInfo || picker.closest('product-info') !== productInfo) return;
        if (!this.currentItem) return;

        this.currentItem.dataset.variantId = '';
        this.currentItem.dataset.price = '0';
        this.currentItem.dataset.quantity = '1';
        this.currentItem.dataset.available = 'false';
        this.currentItem.querySelector('.bt-product-complementary__card')?.classList.add('is-unavailable');

        const soldOut = this.currentItem.querySelector('[data-bt-sold-out]');
        if (soldOut) soldOut.hidden = true;

        this.clearStatus();
        this.updateSummary();
      }

      onVariantChange(event) {
        const variant = event?.data?.variant;
        if (!variant || !this.currentItem) return;

        const productInfo = this.getMainProductInfo();
        if (!productInfo || String(event?.data?.sectionId) !== String(productInfo.dataset.section)) return;

        const submitButton = productInfo.querySelector(`#ProductSubmitButton-${CSS.escape(productInfo.dataset.section)}`);
        const quantityInput = productInfo.querySelector('.quantity__input');
        const quantity = Math.max(0, Number.parseInt(quantityInput?.value || '1', 10) || 0);
        const available =
          Boolean(variant.available) &&
          quantity > 0 &&
          !submitButton?.disabled &&
          submitButton?.getAttribute('aria-disabled') !== 'true';

        this.currentItem.dataset.variantId = variant.id;
        this.currentItem.dataset.price = variant.price;
        this.currentItem.dataset.quantity = String(quantity);
        this.currentItem.dataset.available = String(available);

        const currentCard = this.currentItem.querySelector('.bt-product-complementary__card');
        currentCard?.classList.toggle('is-unavailable', !available);

        const price = this.currentItem.querySelector('[data-bt-item-price]');
        if (price) price.textContent = this.formatMoney(variant.price);

        const quantityLabel = this.currentItem.querySelector('[data-bt-item-quantity]');
        if (quantityLabel) {
          quantityLabel.hidden = quantity <= 1;
          quantityLabel.textContent = `× ${quantity}`;
        }

        const comparePrice = this.currentItem.querySelector('[data-bt-compare-price]');
        if (comparePrice) {
          const hasComparePrice = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
          comparePrice.hidden = !hasComparePrice;
          comparePrice.textContent = hasComparePrice ? this.formatMoney(variant.compare_at_price) : '';
        }

        const variantTitle = this.currentItem.querySelector('[data-bt-current-variant-title]');
        if (variantTitle) {
          const title = variant.public_title || variant.title || '';
          const isDefaultTitle = !title || title.toLowerCase() === 'default title';
          variantTitle.textContent = title;
          variantTitle.classList.toggle('visually-hidden', isDefaultTitle);
        }

        const soldOut = this.currentItem.querySelector('[data-bt-sold-out]');
        if (soldOut) soldOut.hidden = available;

        const imageData = variant.featured_media?.preview_image || variant.featured_image;
        const image = this.currentItem.querySelector('.bt-product-complementary__image');
        if (imageData?.src && image) {
          image.src = imageData.src;
          image.removeAttribute('srcset');
          if (imageData.alt) image.alt = imageData.alt;
        }

        this.clearStatus();
        this.updateSummary();
      }

      hydrateCurrentVariant() {
        if (!this.currentItem) return;

        const productInfo = this.getMainProductInfo();
        const variantInput = productInfo?.querySelector('product-form input[name="id"]');
        const selectedVariantJson = productInfo?.querySelector('variant-selects [data-selected-variant]');

        if (variantInput && !variantInput.value) {
          this.currentItem.dataset.variantId = '';
          this.currentItem.dataset.price = '0';
          this.currentItem.dataset.quantity = '1';
          this.currentItem.dataset.available = 'false';
          return;
        }

        if (!selectedVariantJson) return;

        try {
          const variant = JSON.parse(selectedVariantJson.textContent);
          if (!variant || (variantInput && String(variant.id) !== String(variantInput.value))) return;
          this.onVariantChange({ data: { sectionId: productInfo.dataset.section, variant } });
        } catch (_error) {
          // Server-rendered bundle data remains a safe fallback for single-variant products.
        }
      }

      resolveCurrentVariantId() {
        const productInfo = this.getMainProductInfo();
        const variantInput = productInfo?.querySelector('product-form input[name="id"]');

        // Dawn blanks this field when the chosen option combination is invalid.
        // Falling back here would add the previously selected variant by mistake.
        if (variantInput) return variantInput.value || '';
        return this.currentItem?.dataset.variantId || '';
      }

      createLineItems() {
        this.syncCurrentPurchaseState();
        if (this.currentItem?.dataset.available !== 'true' || !this.resolveCurrentVariantId()) return [];

        const selected = this.getSelectedItems();
        const quantitiesByVariant = new Map();

        selected.forEach((item) => {
          const variantId = item.hasAttribute('data-current-item')
            ? this.resolveCurrentVariantId()
            : item.dataset.variantId;
          const quantity = Math.max(1, Number.parseInt(item.dataset.quantity || '1', 10) || 1);

          if (!variantId) return;
          quantitiesByVariant.set(
            String(variantId),
            (quantitiesByVariant.get(String(variantId)) || 0) + quantity
          );
        });

        return Array.from(quantitiesByVariant, ([id, quantity]) => ({ id: Number(id), quantity }));
      }

      createCartLinesUpdateEvent(items) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: items.map((item) => ({
              merchandiseId: String(item.id),
              quantity: item.quantity,
            })),
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;

        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const cartPromise =
          typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${window.routes?.cart_url || '/cart'}.json`).then((response) => response.json());

        cartPromise
          .then((cart) => {
            if (!cart?.currency) throw new Error('Missing currency in cart response');
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((error) => deferred.reject(error));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      getCartSurface() {
        return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      }

      getSections(cartSurface) {
        if (!cartSurface?.getSectionsToRender) return [];

        return cartSurface
          .getSectionsToRender()
          .map((section) => section.id)
          .filter(Boolean);
      }

      async onSubmit(event) {
        event.preventDefault();
        if (this.submitButton?.disabled || this.submitButton?.classList.contains('is-loading')) return;

        const items = this.createLineItems();
        if (!items.length) {
          this.showStatus(this.dataset.errorMessage, true);
          return;
        }

        const cartSurface = this.getCartSurface();
        const sections = this.getSections(cartSurface);
        const requestBody = {
          items,
          sections_url: window.location.pathname,
        };

        if (sections.length) requestBody.sections = sections;

        this.setLoading(true);
        this.clearStatus();
        cartSurface?.setActiveElement?.(this.submitButton);
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(items);

        try {
          const endpoint =
            window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(requestBody),
          });
          const parsedState = await response.json();

          if (!response.ok || parsedState.status) {
            const requestError = new Error(
              parsedState.description || parsedState.message || this.dataset.errorMessage
            );
            requestError.code = 'INVALID';
            throw requestError;
          }

          this.resolveCartLinesUpdate(linesUpdateDeferred);

          if (
            cartSurface?.tagName === 'CART-NOTIFICATION' &&
            !parsedState.key &&
            Array.isArray(parsedState.items) &&
            parsedState.items.length
          ) {
            parsedState.key = parsedState.items[0].key;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bt-product-complementary',
              productVariantId: items[0].id,
              cartData: parsedState,
            }).catch((error) => console.error('Bundle cart update subscriber failed', error));
          }

          if (cartSurface?.renderContents && parsedState.sections) {
            cartSurface.classList.remove('is-empty');
            cartSurface.renderContents(parsedState);
          } else if (window.routes?.cart_url) {
            window.location.assign(window.routes.cart_url);
            return;
          }

          this.hasSubmitted = true;
          this.showStatus(this.dataset.successMessage, false);
        } catch (error) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'bt-product-complementary',
              productVariantId: items[0]?.id,
              errors: error.message,
              message: error.message,
            });
          }
          this.dispatchCartErrorEvent(
            error.message || this.dataset.errorMessage,
            error.code || 'SERVICE_UNAVAILABLE'
          );
          linesUpdateDeferred?.reject(error);
          this.showStatus(error.message || this.dataset.errorMessage, true);
        } finally {
          this.setLoading(false);
        }
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        this.submitButton.classList.toggle('is-loading', isLoading);
        this.submitButton.toggleAttribute('aria-busy', isLoading);
        this.submitButton.disabled =
          isLoading || this.currentItem?.dataset.available !== 'true' || Boolean(this.hasSubmitted);

        if (this.buttonText && isLoading) this.buttonText.textContent = this.defaultButtonText;
        if (!isLoading) this.updateSummary();
      }

      clearStatus() {
        if (!this.status) return;
        this.status.textContent = '';
        this.status.classList.remove('is-error');
      }

      showStatus(message, isError) {
        if (!this.status) return;
        this.status.textContent = message || '';
        this.status.classList.toggle('is-error', Boolean(isError));
      }
    }
  );
}
